/* eslint-disable @typescript-eslint/no-explicit-any */

'use client';

import { Fragment, useEffect, useState } from 'react';
import { FormContactInfo } from './FormContactInfo';
import { FormContactSell } from './FormContactSell';
import { FormContactSellSteps } from './FormContactSellSteps';
import { FormParts } from './FormParts';
import { FormServiceAppointment } from './FormServiceAppointment';
import { FormStayUpdated } from './FormStayUpdated';
import { FormVanUp } from './FormVanUp';

import { Modal } from '../Modal/Modal';
import { FormType, IFormProps } from './types';
import { useCustomSearchParams } from '@/hooks/useCustomSearchParams';

const getHashSanitized = (hash?: string) => {
  if (!hash || hash?.trim() === '#') return undefined;
  return hash.replaceAll('#', '').trim().toLowerCase();
};

const removeHash = () => {
  if (typeof window === 'undefined') return;
  if ('replaceState' in window.history) {
    window.history.replaceState('', window.document.title, window.location.pathname + window.location.search);
  }
};

// --

const getForm = (props: IFormProps) => {
  switch (props.type) {
    case FormType.contactInfo:
      return <FormContactInfo {...props} />;
    case FormType.contactSell:
      return <FormContactSell {...props} />;
    case FormType.contactSellSteps:
      return <FormContactSellSteps {...props} />;
    case FormType.serviceAppointment:
      return <FormServiceAppointment {...props} />;
    case FormType.stayUpdated:
      return <FormStayUpdated {...props} />;
    case FormType.parts:
      return <FormParts {...props} />;
    case FormType.vanUpfit:
      return <FormVanUp {...props} />;
    default:
      return <Fragment />;
  }
};

export const Form = (props: IFormProps) => {
  const { searchParams } = useCustomSearchParams();
  const [modalOpen, setModalOpen] = useState(false);

  const hashParameter = getHashSanitized(props?.modal_hash_parameter);

  const onHashChanged = () => {
    if (typeof window !== 'undefined') {
      const hashLocation = getHashSanitized(window?.location?.hash);

      if (hashParameter) {
        setModalOpen(hashLocation === hashParameter);
      }
    }
  };

  const onClose = () => {
    setModalOpen(false);
    if (hashParameter) {
      removeHash();
    }
  };

  const onSuccess = () => {
    setTimeout(() => {
      setModalOpen(false);
      if (hashParameter) {
        removeHash();
      }
    }, 2000);
  };

  useEffect(() => {
    let modalOpenStatus: boolean = false;

    if (props.modal && props.modal_url_parameter && !!searchParams.get(props.modal_url_parameter)) {
      modalOpenStatus = true;
    }

    if (typeof window !== 'undefined') {
      const hashLocation = getHashSanitized(window?.location?.hash);
      const hashParameter = getHashSanitized(props?.modal_hash_parameter);

      if (!!hashParameter && hashLocation === hashParameter) {
        modalOpenStatus = true;
      }
    }

    setModalOpen(modalOpenStatus);

    // Hash Change Control
    if (typeof window !== 'undefined') {
      window.addEventListener('hashchange', onHashChanged);
      return () => {
        window.removeEventListener('hashchange', onHashChanged);
      };
    }
  }, []);

  if (props.modal) {
    return (
      <Modal isOpen={modalOpen} onClose={onClose} useMaxWidth={false}>
        {getForm({
          ...props,
          title: props.title || '',
          successCallback: onSuccess,
        } as any)}
      </Modal>
    );
  }

  return getForm(props);
};
